import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  AfterViewInit,
  inject,
  NgZone,
  ViewChild,
  Input,
} from '@angular/core';
import { CommonModule } from '@angular/common';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  color: string;
  rotation: number;
  rotationSpeed: number;
  alpha: number;
  decay: number;
  gravity: number;
  friction: number;
}

@Component({
  selector: 'app-confetti-canvas',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './confetti-canvas.html',
  styleUrl: './confetti-canvas.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConfettiCanvasComponent implements AfterViewInit, OnDestroy {
  @ViewChild('confettiCanvas', { static: true })
  private readonly canvasRef!: ElementRef<HTMLCanvasElement>;

  @Input() triggerBurstOnInit = true;
  @Input() burstDelay = 300;
  @Input() particleCount = 100;
  @Input() colors: string[] = ['#f97316', '#fb923c', '#fdba74', '#94a3b8', '#cbd5e1'];

  private readonly ngZone = inject(NgZone);
  private ctx!: CanvasRenderingContext2D;
  private particles: Particle[] = [];
  private animationId: number | null = null;

  ngAfterViewInit(): void {
    this.setupCanvas();
    this.ngZone.runOutsideAngular(() => {
      this.startAnimation();
      if (this.triggerBurstOnInit) {
        setTimeout(() => this.createBurst(), this.burstDelay);
      }
    });
  }

  ngOnDestroy(): void {
    if (this.animationId !== null) {
      cancelAnimationFrame(this.animationId);
    }
    window.removeEventListener('resize', this.onResize);
  }

  private setupCanvas(): void {
    const canvas = this.canvasRef.nativeElement;
    this.ctx = canvas.getContext('2d')!;
    this.onResize();
    window.addEventListener('resize', this.onResize);
  }

  private readonly onResize = (): void => {
    const canvas = this.canvasRef.nativeElement;
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
  };

  protected onCanvasClick(event: MouseEvent): void {
    const x = event.clientX;
    const y = event.clientY;
    for (let i = 0; i < 20; i++) {
      this.particles.push(this.createParticle(x, y));
    }
  }

  createBurst(x?: number, y?: number): void {
    const canvas = this.canvasRef.nativeElement;
    const centerX = x ?? canvas.width / 2;
    const centerY = y ?? canvas.height / 2;

    for (let i = 0; i < this.particleCount; i++) {
      this.particles.push(this.createParticle(centerX, centerY));
    }
  }

  private createParticle(x: number, y: number): Particle {
    const angle = Math.random() * Math.PI * 2;
    const velocity = Math.random() * 15 + 5;

    return {
      x,
      y,
      vx: Math.cos(angle) * velocity,
      vy: Math.sin(angle) * velocity - 5,
      size: Math.random() * 8 + 4,
      color: this.colors[Math.floor(Math.random() * this.colors.length)],
      rotation: Math.random() * 360,
      rotationSpeed: (Math.random() - 0.5) * 10,
      alpha: 1,
      decay: Math.random() * 0.01 + 0.005,
      gravity: 0.2,
      friction: 0.96,
    };
  }

  private startAnimation(): void {
    const animate = (): void => {
      const canvas = this.canvasRef.nativeElement;
      this.ctx.clearRect(0, 0, canvas.width, canvas.height);

      for (let i = this.particles.length - 1; i >= 0; i--) {
        const p = this.particles[i];
        this.updateParticle(p, canvas.height);
        this.drawParticle(p);

        if (p.alpha <= 0) {
          this.particles.splice(i, 1);
        }
      }

      this.animationId = requestAnimationFrame(animate);
    };

    animate();
  }

  private updateParticle(p: Particle, height: number): void {
    p.x += p.vx;
    p.y += p.vy;
    p.vy += p.gravity;
    p.vx *= p.friction;
    p.rotation += p.rotationSpeed;
    p.alpha -= p.decay;

    // Bounce off floor
    if (p.y + p.size > height) {
      p.y = height - p.size;
      p.vy *= -0.5;
    }
  }

  private drawParticle(p: Particle): void {
    this.ctx.save();
    this.ctx.translate(p.x, p.y);
    this.ctx.rotate((p.rotation * Math.PI) / 180);
    this.ctx.globalAlpha = p.alpha;
    this.ctx.fillStyle = p.color;
    this.ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size);
    this.ctx.restore();
  }
}

